/**
 * actionMerging — view-layer merge of create + association actions.
 *
 * When a proposal contains an `ahoy_object_created` action followed by
 * `ahoy_association_added` actions for the same entity_id, this module
 * merges them into a single visual unit (MergedAction) so the UI can
 * render a single card instead of separate cards.
 *
 * The underlying ProposedAction[] from the API is NEVER mutated.
 * This is purely a view-layer transformation.
 */

import type { EnrichedActionData, ProposedAction } from '../core/types';
import {
  isAssociationAddedPayload,
  isObjectCreatedPayload,
} from '../core/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MergedAssociation {
  /** Original association action (preserved for selection/approval) */
  action: ProposedAction;
  /** e.g. "ahoy_deal_to_company" */
  associationType: string;
  /** The entity being associated with the created entity */
  targetEntityId: string;
  /** Display label, entity type, exists_in_crm from backend enrichment */
  enrichedData?: EnrichedActionData;
}

export interface MergedAction {
  /** The create action — primary UI representation */
  primaryAction: ProposedAction;
  /** Association actions absorbed into this card (may be empty) */
  mergedAssociations: MergedAssociation[];
  /** All action IDs in this group (primary + absorbed), for selection */
  allActionIds: number[];
}

export type MergedOrOriginal =
  | { type: 'merged'; merged: MergedAction }
  | { type: 'original'; action: ProposedAction };

// ─── Core merge function ──────────────────────────────────────────────────────

/**
 * Create actions that are NOT eligible for create+association merging.
 *
 * Every `ahoy_object_created` action is mergeable EXCEPT these meta-creations:
 * property-type and entity-type creation have bespoke edit forms and carry no
 * real entity associations, so folding is meaningless for them. Every other
 * created object — built-in (contact / deal / risk / note) OR custom `org_*` —
 * folds in its association actions (stage, company, contact, …) the same way
 * deals do, so the experience is consistent across all pipelinable/associable
 * objects.
 *
 * Notes still fold their owner / stage / parent-link associations into a single
 * NoteCard (see MergedCreateActionCard's ahoy_note branch).
 */
const NON_MERGEABLE_CREATE_TYPES = new Set([
  'ahoy_property_type',
  'ahoy_entity_type',
]);

/** Whether a created entity type folds its associations into one card. */
function isMergeableCreateType(entityTypeName: string): boolean {
  return !NON_MERGEABLE_CREATE_TYPES.has(entityTypeName);
}

/**
 * Timeline associations (source or target ends with `_timeline`) must never be
 * folded into a create card — they represent timeline links, not entity
 * relationships, and are rendered as their own (error) row. Parsed inline to
 * keep this low-tier util free of registry imports.
 */
function isTimelineAssociationType(associationTypeName: string): boolean {
  const idx = associationTypeName.indexOf('_to_');
  if (idx === -1) {
    return associationTypeName.endsWith('_timeline');
  }
  const source = associationTypeName.slice(0, idx);
  const target = associationTypeName.slice(idx + '_to_'.length);
  return source.endsWith('_timeline') || target.endsWith('_timeline');
}

/**
 * Merge create + association actions into visual groups.
 *
 * Rules:
 * - Only `ahoy_object_created` actions for MERGEABLE_ENTITY_TYPES are merged.
 * - Every `ahoy_association_added` whose `entity_id` matches a mergeable created entity is absorbed.
 * - `ahoy_association_removed` is NEVER absorbed.
 * - Non-mergeable create actions and all other actions pass through as `original`.
 * - Output preserves `execution_order` sort order.
 *
 * @param actions     Actions in execution_order (as returned by API)
 * @param enrichedDataMap  Optional map of action.id → EnrichedActionData for associations
 */
export function mergeActions(
  actions: ProposedAction[],
  enrichedDataMap?: Record<number, EnrichedActionData>
): MergedOrOriginal[] {
  // Build set of entity_ids that have a mergeable create action.
  const createdEntityMap = new Map<string, ProposedAction>();
  for (const action of actions) {
    if (
      isObjectCreatedPayload(action.payload) &&
      isMergeableCreateType(action.payload.entity_type_name)
    ) {
      createdEntityMap.set(action.payload.entity_id, action);
    }
  }

  // Set of action IDs that are absorbed into a merged group.
  const absorbedIds = new Set<number>();

  // Build merged associations for each create action.
  const associationsByCreatedEntity = new Map<string, MergedAssociation[]>();

  for (const action of actions) {
    if (!isAssociationAddedPayload(action.payload)) {
      continue;
    }
    const sourceEntityId = action.payload.entity_id;
    if (!createdEntityMap.has(sourceEntityId)) {
      continue;
    }
    // Never fold timeline associations into a create card — they're invalid as
    // entity relationships and pass through as their own (error-rendered) row.
    if (
      isTimelineAssociationType(action.payload.payload.association_type_name)
    ) {
      continue;
    }
    // This association's source entity was created — absorb it.
    absorbedIds.add(action.id);
    const existing = associationsByCreatedEntity.get(sourceEntityId) ?? [];
    const enrichedData =
      enrichedDataMap?.[action.id] ?? action.enriched_data ?? undefined;
    existing.push({
      action,
      associationType: action.payload.payload.association_type_name,
      targetEntityId: action.payload.payload.target_entity_id,
      enrichedData,
    });
    associationsByCreatedEntity.set(sourceEntityId, existing);
  }

  // Build output preserving execution_order.
  const result: MergedOrOriginal[] = [];

  for (const action of actions) {
    if (absorbedIds.has(action.id)) {
      // Skip — already absorbed into a create group.
      continue;
    }

    if (
      isObjectCreatedPayload(action.payload) &&
      isMergeableCreateType(action.payload.entity_type_name)
    ) {
      const entityId = action.payload.entity_id;
      const mergedAssociations =
        associationsByCreatedEntity.get(entityId) ?? [];
      const allActionIds = [
        action.id,
        ...mergedAssociations.map(a => a.action.id),
      ];
      result.push({
        type: 'merged',
        merged: {
          primaryAction: action,
          mergedAssociations,
          allActionIds,
        },
      });
      continue;
    }

    result.push({ type: 'original', action });
  }

  return result;
}

// ─── Count helper ─────────────────────────────────────────────────────────────

/**
 * Returns the number of visual items in a merged list.
 * Merged groups count as 1 regardless of how many constituent actions they contain.
 */
export function getMergedActionCount(items: MergedOrOriginal[]): number {
  return items.length;
}
